import { useState, useCallback, useEffect } from "react";
import { DAYS, formatTime, toLocalDateStr, isSameDay, isEventPast, workshopsForDate, pillLabel, spaceLabel } from "./calendarUtils";
import { useAdminApi } from "../pages/admin/adminUtils";
import { EventPill, WorkshopBanner } from "./CalendarPills";

export function MonthView({ calendarCells, today, adminMode, getVisibleClasses, getPublicClasses,
  renderWorkshopBanners, setSelectedEvent, setSelectedDate }) {
  return (
    <div className="cal-grid">
      <div className="day-headers">{DAYS.map(d=><div key={d} className="day-header">{d}</div>)}</div>
      <div className="month-grid">
        {calendarCells.map((date,i)=>{
          if(!date) return <div key={`e-${i}`} className="day-cell empty"/>;
          const isToday = isSameDay(date, today);
          const evts    = adminMode ? getVisibleClasses(date) : getPublicClasses(date);
          return (
            <div key={date.toISOString()} className={`day-cell ${isToday?"today":""}`}>
              <span className="day-num">{date.getDate()}</span>
              {renderWorkshopBanners(date)}
              {evts.map(evt=>(
                <EventPill key={evt.id} event={evt} compact={true}
                  adminMode={adminMode} cancelled={evt._cancelled}
                  dateStr={toLocalDateStr(date)}
                  onClick={e=>{ if ((e._isPrivate && !adminMode) || e._isBlock) return; setSelectedEvent(e);setSelectedDate(date);}} />
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function WeekView({ weekDates, today, adminMode, getVisibleClasses, getPublicClasses,
  workshops, setSelectedEvent, setSelectedDate, setSelectedWorkshop }) {
  return (
    <div className="week-grid">
      {weekDates.map(date=>{
        const evts     = adminMode ? getVisibleClasses(date) : getPublicClasses(date);
        const dateStr  = toLocalDateStr(date);
        const wsForDay = workshopsForDate(dateStr, workshops);
        const isToday  = isSameDay(date, today);
        return (
          <div key={date.toISOString()} className={`week-col ${isToday?"today":""}`}>
            <div className="week-col-header">
              <span className="week-day-name">{DAYS[date.getDay()]}</span>
              <span className="week-day-num">{date.getDate()}</span>
            </div>
            {wsForDay.map(w => (
              <WorkshopBanner key={w.id} workshop={w} dateStr={dateStr} onClick={setSelectedWorkshop} adminMode={adminMode} />
            ))}
            {evts.map(evt => {
              const past = !adminMode && isEventPast(dateStr, evt.time, evt.duration);
              return (
                <button key={evt.id}
                  data-subj={evt._isPrivate ? undefined : (evt.subject || "")} className={`week-event-pill ${evt._isPrivate?"private":evt.isHybrid?"hybrid":evt.venue==="venue_online"?"online":"inperson"} ${evt._cancelled?"cancelled-pill":""} ${past && !adminMode?"past-pill":""}`}
                  onClick={() => { if (past && !adminMode) return; if ((evt._isPrivate && !adminMode) || evt._isBlock) return; setSelectedEvent(evt); setSelectedDate(date); }}>
                  <span className="week-event-time">{formatTime(evt.time)}</span>
                  <span className="week-event-title">{evt._isPrivate ? (evt._isBlock ? evt.title : "Private") : pillLabel(evt)}</span>
                  {!evt._isPrivate && <span className="week-event-label">{evt._cancelled?"Cancelled":evt.isHybrid?"Hybrid":evt.venue==="venue_online"?"Online":"Studio"}</span>}
                  {evt._space && !evt._cancelled && !past && !adminMode && (
                    <span className={`event-space${evt._space.full ? " event-space--full" : ""}`}>{spaceLabel(evt._space, true)}</span>
                  )}
                </button>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}

export function DayView({ focusDay, adminMode, getVisibleClasses, getPublicClasses,
  workshops, setSelectedEvent, setSelectedDate, setSelectedWorkshop }) {
  const dateStr  = toLocalDateStr(focusDay);
  const evts     = adminMode ? getVisibleClasses(focusDay) : getPublicClasses(focusDay);
  const wsForDay = workshopsForDate(dateStr, workshops);
  const isEmpty  = evts.length === 0 && wsForDay.length === 0;

  return (
    <div className="day-view">
      <div className="day-view-date">
        {focusDay.toLocaleDateString("en-US",{weekday:"long",month:"long",day:"numeric",year:"numeric"})}
      </div>
      {wsForDay.map(w=>(
        <button key={w.id} data-subj={w.subject || ""} className="day-workshop-card" onClick={()=>setSelectedWorkshop(w)}>
          <div className="day-event-time-col" style={{color:"var(--type-color, var(--color-workshop))"}}>Workshop</div>
          <div className="day-event-info">
            <span className="day-event-title">{w.title}</span>
            <span className="day-event-sub">
              {w.days.find(d=>d.date===dateStr)?.sessions.map(s=>`${s.label} ${formatTime(s.time)}`).join(" · ")}
            </span>
          </div>
        </button>
      ))}
      {isEmpty && <p className="no-classes">No classes scheduled today.</p>}
      {evts.map(evt => {
        const past = !adminMode && isEventPast(dateStr, evt.time, evt.duration);
        return (
          <button key={evt.id}
            data-subj={evt._isPrivate ? undefined : (evt.subject || "")} className={`day-event-card ${evt.isHybrid?"hybrid":evt.venue==="venue_online"?"online":"inperson"} ${evt._cancelled?"cancelled-pill":""} ${past?"past-pill":""}`}
            onClick={() => { if (past && !adminMode) return; if ((evt._isPrivate && !adminMode) || evt._isBlock) return; setSelectedEvent(evt); setSelectedDate(focusDay); }}>
            <div className="day-event-time-col">{formatTime(evt.time)}</div>
            <div className="day-event-info">
              <span className="day-event-title">
                {evt.title}{evt._cancelled&&<span className="cancelled-label"> — Cancelled</span>}
              </span>
              <span className="day-event-sub">
                {evt.isHybrid?"In Studio + Online":evt.venue==="venue_online"?"Online":"In Studio"}{evt.teacher ? ` · ${evt.teacher}` : ""}
                {evt._space && !evt._cancelled && !past && !adminMode && (
                  <span className={`event-space${evt._space.full ? " event-space--full" : ""}`}> · {spaceLabel(evt._space)}</span>
                )}
              </span>
            </div>
          </button>
        );
      })}
    </div>
  );
}
export function ListView({ adminMode, classes, cancelledDates, dateOverrides, privateBookings, today, setSelectedEvent, setSelectedDate, venues = [] }) {
  const { API, AUTH: AUTH_HEADERS } = useAdminApi();
  const [expanded, setExpanded] = useState({});
  const [rosters, setRosters]   = useState({});
  const [loading, setLoading]   = useState({});
  const [counts, setCounts]     = useState({});

  // Fetch booking counts for all upcoming class occurrences
  useEffect(() => {
    if (!adminMode) return;
    const days = 60;
    const pairs = [];
    for (let i = 0; i < days; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      const dow = date.getDay();
      const dateStr = toLocalDateStr(date);
      classes
        .filter(c => c.dayOfWeek === dow && (!c.startDate || dateStr >= c.startDate) && (!c.endDate || dateStr <= c.endDate))
        .forEach(c => pairs.push({ classId: c.id, dateStr }));
    }
    pairs.forEach(async ({ classId, dateStr }) => {
      try {
        const res  = await fetch(`${API}/bookings?classId=${encodeURIComponent(classId)}&date=${encodeURIComponent(dateStr)}`, { headers: AUTH_HEADERS });
        const data = await res.json();
        const key  = `${classId}__${dateStr}`;
        setCounts(prev => ({ ...prev, [key]: (data.bookings ?? []).filter(b => b.status === "confirmed").length }));
      } catch (err) { console.error("ListView: failed to load booking count", classId, dateStr, err); }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminMode, classes, API]);

  const toggleRoster = useCallback(async (classId, dateStr) => {
    const key = `${classId}__${dateStr}`;
    if (expanded[key]) { setExpanded(e => ({ ...e, [key]: false })); return; }
    setExpanded(e => ({ ...e, [key]: true }));
    if (rosters[key]) return;
    setLoading(l => ({ ...l, [key]: true }));
    try {
      const res  = await fetch(`${API}/bookings?classId=${encodeURIComponent(classId)}&date=${encodeURIComponent(dateStr)}`, { headers: AUTH_HEADERS });
      const data = await res.json();
      setRosters(r => ({ ...r, [key]: (data.bookings ?? []).filter(b => b.status === "confirmed") }));
    } catch { setRosters(r => ({ ...r, [key]: [] })); }
    finally  { setLoading(l => ({ ...l, [key]: false })); }
  }, [expanded, rosters, API, AUTH_HEADERS]);

  const passLabel = (b) => {
    if (!b.passId) return b.paymentType === "dropin" ? "Drop-in" : (b.paymentType || "—");
    const p = b.passId;
    if (p.type === "unlimited")  return "Unl";
    if (p.type === "member2x")   return "2x";
    if (p.type === "memberunl")  return "Unl";
    if (p.classesTotal === null) return p.type;
    return `${p.classesUsed}/${p.classesTotal}`;
  };

  const days = 60;
  const rows = [];

  for (let i = 0; i < days; i++) {
    const date = new Date(today);
    date.setDate(today.getDate() + i);
    const dow = date.getDay();
    const dateStr = toLocalDateStr(date);

    const regular = classes
      .filter(c => c.dayOfWeek === dow && (!c.startDate || dateStr >= c.startDate) && (!c.endDate || dateStr <= c.endDate))
      .map(c => {
        const key = `${c.id}__${dateStr}`;
        const override = dateOverrides.get(key);
        return { ...c, ...(override || {}), _cancelled: cancelledDates.has(key), _isPrivate: false };
      });

    const privates = adminMode
      ? privateBookings
          .filter(b => b.date === dateStr)
          .map(b => {
            const timeRaw = b.classId.split("__")[2] ?? "";
            const time = timeRaw ? `${timeRaw.slice(0,2)}:${timeRaw.slice(2)}` : b.time ?? "";
            return { id: b._id, _id: b._id, classId: b.classId, title: b._isBlock ? (b.note || "Blocked") : "Private", time, duration: b._isBlock ? b.duration : 60, _isPrivate: true, _isBlock: !!b._isBlock, _cancelled: false, location: b.location };
          })
      : [];

    [...regular, ...privates]
      .sort((a, b) => (a.time || "").localeCompare(b.time || ""))
      .forEach(cls => rows.push({ date: new Date(date), dateStr, cls }));
  }

  if (rows.length === 0) return <div className="list-view list-view--empty">No classes scheduled in the next 60 days.</div>;

  let lastDateStr = null;

  return (
    <div className="list-view">
      {rows.map(({ date, dateStr, cls }, idx) => {
        const isNewDay = dateStr !== lastDateStr;
        lastDateStr = dateStr;
        const isToday = isSameDay(date, today);
        const label = isToday
          ? "Today"
          : date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
        const key = `${cls.id ?? cls._id}__${dateStr}`;
        const rosterOpen    = expanded[key];
        const rosterLoading = loading[key];
        const roster        = rosters[key] ?? [];

        return (
          <div key={`${dateStr}-${cls.id}-${idx}`} className="list-view__row-wrap">
            {isNewDay && (
              <div className={`list-view__date-header${isToday ? " list-view__date-header--today" : ""}`}>
                {label}
                {!isToday && <span className="list-view__date-dow">{date.toLocaleDateString("en-US", { weekday: "long" })}</span>}
              </div>
            )}
            <div
              data-subj={cls._isPrivate ? undefined : (cls.subject || "")} className={`list-view__row${cls._cancelled ? " list-view__row--cancelled" : ""}${cls._isPrivate ? " list-view__row--private" : cls.venue === "venue_online" ? " list-view__row--online" : cls.isHybrid ? " list-view__row--hybrid" : " list-view__row--studio"}`}
              onClick={cls._cancelled || cls._isPrivate ? undefined : () => { setSelectedEvent(cls); setSelectedDate(date); }}
              role={cls._cancelled || cls._isPrivate ? undefined : "button"}
            >
              <span className="list-view__time">{formatTime(cls.time)}</span>
              <div className="list-view__body">
                <span className="list-view__title">
                  {cls.title}
                  {cls._cancelled && <span className="list-view__cancelled-badge">Cancelled</span>}
                  {cls._isPrivate && <span className="list-view__private-badge">{cls._isBlock ? "Blocked" : "Private"}</span>}
                </span>
                <div className="list-view__meta">
                  {cls.teacher && <span className="list-view__teacher">{cls.teacher}</span>}
                  {cls.teacher && <span className="list-view__meta-sep">·</span>}
                  <span className="list-view__venue">{cls._isPrivate ? (cls.location ? cls.location.charAt(0).toUpperCase() + cls.location.slice(1) : "") : cls.venue === "venue_online" ? "Online" : (venues.find(v => v.id === cls.venue)?.name || "In Studio")}{!cls._isPrivate && cls.isHybrid ? " + Online" : ""}</span>
                </div>
              </div>
              {adminMode && !cls._isPrivate && !cls._cancelled && (
                <button
                  className={`list-view__count-btn${rosterOpen ? " active" : ""}`}
                  onClick={e => { e.stopPropagation(); toggleRoster(cls.id, dateStr); }}
                >
                  {rosterLoading ? "…" : rosterOpen ? `${roster.length} attendee${roster.length !== 1 ? "s" : ""} ▲` : counts[key] !== undefined ? `${counts[key]} attendee${counts[key] !== 1 ? "s" : ""} ▾` : "Attendees ▾"}
                </button>
              )}
            </div>
            {rosterOpen && (
              <div className="list-view__roster">
                {roster.length === 0
                  ? <span className="list-view__roster-empty">No bookings yet</span>
                  : roster.map(b => (
                      <div key={b._id} className="list-view__roster-row">
                        <span className="list-view__roster-name">{b.studentName}</span>
                        <span className="list-view__roster-pass">{passLabel(b)}</span>
                        {b.checkedIn && <span className="list-view__roster-checked">✓</span>}
                      </div>
                    ))
                }
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
